/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import {Component, OnInit, Input, ViewChild} from "@angular/core";
import {Group} from "../../models/group";
import {Permission} from "../../models/permission";
import {Page, PagePermissionService} from "../../services/page-permission.service";
import {GroupService} from "../../services/group.service";
import {UserGroupService} from "../../services/user-group.service";
import {RoleNotificationService} from "../../services/role-notification.service";
import {EmGroup} from "../../models/em-group";
import {GroupPermission} from "../../models/group";
import { UserPagePermission } from "app/services/user-page-permission.service";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from "../simple-success-error-dialog-box/simple-success-error-dialog-box.component";


declare var $: any;

@Component({
  selector: 'app-rolemanager-dialog',
  templateUrl: './rolemanager-dialog.component.html',
  styleUrls: ['./rolemanager-dialog.component.css']
})
export class RoleManagerDialogComponent implements OnInit {

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  _userRole: Group;
  accessiblePages: Page[];

  @Input() set userRole(userRole: Group) {
    this._userRole = userRole;

    this.editableUserRole = JSON.parse(JSON.stringify(userRole));

    // The userRole might not have all the page permissions (a new page might have been created or removed), so update it so it has all the page permissions
    this.editableUserRole.pagePermissions = this.pagePermissionService.getEmptyPagePermissions();
    for(const pagePermission of Object.keys(this._userRole.pagePermissions)) {
      this.editableUserRole.pagePermissions[pagePermission] = this._userRole.pagePermissions[pagePermission];
    }

  };

  editableUserRole: Group;
  allGroups: EmGroup[];

  // Database names differ from names used in the GUI. This is the translation map.
  private objectsForPermissions = {
    "ANTENNA" : "ANTENNA",
    "GROUP" : "GROUP",
    "LOCATION" : "LOCATION",
    "OBJECT" : "OBJECT",
    "USER" : "PEOPLE",
    "ACTION" : "REACTION",
    "READER" : "READER",
    "RULE" : "RULE",
    "ASSET" : "TAG",
    "EVENT" : "TRIGGER",
    "READZONE" : "ZONE"
  };

  constructor(public pagePermissionService: PagePermissionService, public userGroupService: UserGroupService, public roleNotificationService: RoleNotificationService,
              public groupService: GroupService, public userPagePermission: UserPagePermission) {
    this.groupService.getAllGroups().then(
      response => {
        this.allGroups = response.sort();
      },
      error => {
        this.handleError(error);
      }
    );
  }

  ngOnInit(): void {
    this.userPagePermission.getListOfAccessiblePages().then((pages) => {
      this.accessiblePages = pages;
  })
}

  /** 
   * Return the Page Permission Keys sorted by the Page displayed label 
   */
  pagePermissionKeys(): string[] {
    let pages = this.accessiblePages.sort((page1, page2) => page1.label.localeCompare(page2.label));
    return pages.map(page => page.permissionType);
  }

  objectsForPermissionsKeys(): string[] {
    // Angular does not allow Object.keys to appear in html markup, so we have to use a function to call this
    return Object.keys(this.objectsForPermissions).sort()
  }

  groupInRole(group: EmGroup): boolean {
    // Returns true if this group is in the editableUserRole.groupPermissions, false otherwise
    if (this.editableUserRole.groupPermissions.values == null) {
      return false;
    }

    // Loop through all the group permissions in the selected editableUserRole to find if the passed in group is part of this
    for(const groupInRole of this.editableUserRole.groupPermissions.values) {
      if (groupInRole.group.id == group.id) {
        return true;
      }
    }

    return false;
  }

  addGroupToUserRole(group: EmGroup): void {
    const groupPermission = new GroupPermission();
    groupPermission.group = group;
    groupPermission.permission = new Permission(false, false, false, false);
    this.editableUserRole.groupPermissions.values.push(groupPermission);
  }

  cancel(): void {
    $("#roleEditModal").hide();
  }

  deleteGroupQuery(groupId: number): void {
    this.deleteGroup(groupId);
  }

  deleteGroup(groupId: number): void {
    let groupToDelete = -1;
    let index = 0;

    // Find the group in the groupPermissions array that has the id we are looking for
    for(const groupPermission of this.editableUserRole.groupPermissions.values) {
      if (groupPermission.group.id == groupId) {
        groupToDelete = index;
        break;
      }

      index++;
    }

    // Delete the groupPermission
    if (groupToDelete != -1) {
      this.editableUserRole.groupPermissions.values.splice(groupToDelete, 1);
    }
  }

  createOrUpdateRole(): void {
    // If the role has an id, we are updating a current role
    if (this.editableUserRole.id) {
      this.userGroupService.updateUserGroup(this.editableUserRole).then(
        response => {
          this._userRole = this.editableUserRole;
          $("#roleEditModal").hide();
          // inform listeners that something has been updated
          this.roleNotificationService.refreshRoles();
        },
        error => {
          this.handleError(error);
        }
      );
    } else {
      // If the role does not have an id, we are creating a new role
      this.userGroupService.createUserGroup(this.editableUserRole).then(
        response => {
          this._userRole = this.editableUserRole;
          $("#roleEditModal").hide();
          // inform listeners that something has been updated
          this.roleNotificationService.refreshRoles();
        },
        error => {
          this.handleError(error);
        }
      );
    }

  }

  indexTracker(index: number, value: any): any {
    return index;
  }

  handleError(error: any): void {
    console.error("DevicesComponent.handleError() - Error: " + error);
    if (error != null) {
      const response: Response = error;
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, response.text().toString());
    }
  }

}
